import { Stack } from '@mui/material';
import { MessageStatus } from '@schooly/api';
import { FC, PropsWithChildren, useCallback, useState } from 'react';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { toggleMultipleValueArrayProperty } from '../utils';
import { MessageStatusTagSelect, MessageStatusTagSelectProps } from './MessageStatusTagSelect';

type MessageStatusExpandedSelectProps = PropsWithChildren<{
  selectedStatuses: MessageStatus[];
  onApply: (v: MessageStatus[]) => void;
  onClear: () => void;
  onClose: () => void;
}>;

const STATUSES = [MessageStatus.Published, MessageStatus.Draft, MessageStatus.Submitted];

export const MessageStatusExpandedSelect: FC<MessageStatusExpandedSelectProps> = ({
  selectedStatuses: initialSelectedStatuses,
  onApply,
  onClose,
  onClear,
}) => {
  const [selectedStatuses, setSelectedStatuses] =
    useState<MessageStatus[]>(initialSelectedStatuses);

  const toggleStatuses = useCallback((v: MessageStatus[]) => {
    setSelectedStatuses((statuses) => toggleMultipleValueArrayProperty(statuses, v));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedStatuses);
    onClose();
  }, [selectedStatuses, onApply, onClose]);

  const renderContent = useCallback(() => {
    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {renderMessageStatusTags({
            statuses: STATUSES,
            tagProps: (status) => ({
              variant: selectedStatuses.includes(status) ? 'filled' : undefined,
            }),
            onClick: (status) => toggleStatuses([status]),
          })}
        </Stack>
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [handleApply, onClear, toggleStatuses, selectedStatuses]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedStatuses.length > 0}
      renderContent={renderContent}
      onClose={onClose}
      onClear={onClear}
    >
      {renderMessageStatusTags({
        statuses: selectedStatuses,
        tagProps: { size: 'small' },
        onDelete: (status) => toggleStatuses([status]),
      })}
    </ExpandedSelect>
  );
};

type RenderMessageStatusTagsParams = {
  statuses: MessageStatus[];
  onDelete?: (v: MessageStatus) => void;
  onClick?: (v: MessageStatus) => void;
  tagProps?:
    | Omit<MessageStatusTagSelectProps, 'status'>
    | ((v: MessageStatus) => Omit<MessageStatusTagSelectProps, 'status'>);
};

export const renderMessageStatusTags = ({
  statuses,
  onDelete,
  onClick,
  tagProps,
}: RenderMessageStatusTagsParams) =>
  statuses.map((v) => (
    <MessageStatusTagSelect
      key={v}
      status={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
